// import { useEffect, useRef, useState, useCallback } from "react";

// type Options = IntersectionObserverInit & {
//     once?: boolean;
// };

// export function useMultiInViewport<T extends string>(
//     keys: readonly T[],
//     options?: Options
// ) {
//     const { once = true, ...observerOptions } = options || {};

//     // Map element -> key (type-safe, không cần dataset)
//     const elementKeyMap = useRef(new WeakMap<Element, T>());

//     // Lưu danh sách element theo key
//     const elementsRef = useRef<Partial<Record<T, HTMLElement>>>({});

//     // State visible
//     const [visibleMap, setVisibleMap] = useState<Record<T, boolean>>(() =>
//         keys.reduce((acc, key) => {
//             acc[key] = false;
//             return acc;
//         }, {} as Record<T, boolean>)
//     );

//     useEffect(() => {
//         const observer = new IntersectionObserver((entries) => {
//             entries.forEach((entry) => {
//                 const key = elementKeyMap.current.get(entry.target);
//                 if (!key) return;

//                 setVisibleMap((prev) => ({
//                     ...prev,
//                     [key]: entry.isIntersecting,
//                 }));

//                 if (once && entry.isIntersecting) {
//                     observer.unobserve(entry.target);
//                 }
//             });
//         }, observerOptions);

//         // observe tất cả element
//         keys.forEach((key) => {
//             const el = elementsRef.current[key];
//             if (!el) return;

//             elementKeyMap.current.set(el, key);
//             observer.observe(el);
//         });

//         return () => observer.disconnect();
//     }, [once, observerOptions]);

//     // set ref theo key
//     const setRef = useCallback(
//         (key: T) => (el: HTMLElement | null) => {
//             if (el) {
//                 elementsRef.current[key] = el;
//             }
//         },
//         []
//     );

//     return {
//         setRef,
//         visibleMap,
//     };
// }


import { useEffect, useRef, useState, useCallback } from "react";

type Options = IntersectionObserverInit & {
    once?: boolean;
};

export function useMultiInViewport<T extends string>(
    keys: readonly T[],
    options?: Options
) {
    const { once = true, ...observerOptions } = options || {};

    const elementKeyMap = useRef(new WeakMap<Element, T>());
    const elementsRef = useRef<Partial<Record<T, HTMLElement>>>({});

    // 👀 visible
    const [visibleMap, setVisibleMap] = useState<Record<T, boolean>>(() =>
        keys.reduce((acc, key) => {
            acc[key] = false;
            return acc;
        }, {} as Record<T, boolean>)
    );

    // ⏳ loading
    const [loadingMap, setLoadingMap] = useState<Record<T, boolean>>(() =>
        keys.reduce((acc, key) => {
            acc[key] = true;
            return acc;
        }, {} as Record<T, boolean>)
    );

    // ✅ đã load API chưa
    const [loadedMap, setLoadedMap] = useState<Record<T, boolean>>(() =>
        keys.reduce((acc, key) => {
            acc[key] = false;
            return acc;
        }, {} as Record<T, boolean>)
    );

    // Dùng ref để tránh observerOptions (inline object) làm effect re-run mỗi render
    const observerOptionsRef = useRef(observerOptions);
    const onceRef = useRef(once);

    useEffect(() => {
        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                const key = elementKeyMap.current.get(entry.target);
                if (!key) return;

                setVisibleMap((prev) => ({
                    ...prev,
                    [key]: entry.isIntersecting,
                }));

                if (onceRef.current && entry.isIntersecting) {
                    observer.unobserve(entry.target);
                }
            });
        }, observerOptionsRef.current);

        keys.forEach((key) => {
            const el = elementsRef.current[key];
            if (!el) return;

            elementKeyMap.current.set(el, key);
            observer.observe(el);
        });

        return () => observer.disconnect();
    }, [keys]);

    const setRef = useCallback(
        (key: T) => (el: HTMLElement | null) => {
            if (el) {
                elementsRef.current[key] = el;
            }
        },
        []
    );

    // ===== loading =====
    const setLoading = useCallback((key: T, value: boolean) => {
        setLoadingMap((prev) => ({
            ...prev,
            [key]: value,
        }));

        // 🔥 auto set loaded khi loading = false
        if (value === false) {
            setLoadedMap((prev) => ({
                ...prev,
                [key]: true,
            }));
        }
    }, []);

    const resetAllLoading = useCallback(() => {
        setLoadingMap((prev) => {
            const next = { ...prev };
            keys.forEach((key) => {
                next[key] = false;
            });
            return next;
        });
    }, [keys]);

    // ===== loaded =====
    const setLoaded = useCallback((key: T, value: boolean) => {
        setLoadedMap((prev) => ({
            ...prev,
            [key]: value,
        }));
    }, []);

    const resetAllLoaded = useCallback(() => {
        setLoadedMap((prev) => {
            const next = { ...prev };
            keys.forEach((key) => {
                next[key] = false;
            });
            return next;
        });
    }, [keys]);

    return {
        setRef,
        visibleMap,

        loadingMap,
        setLoading,
        resetAllLoading,

        loadedMap,
        setLoaded,
        resetAllLoaded,
    };
}